import React, { Component } from 'react';
import { string, bool } from 'prop-types';
import PropTypes from 'prop-types';
import { NamedLink } from '..';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { userDisplayNameAsString, truncateText } from '../../util/data';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { FaRegHeart, FaHeart } from 'react-icons/fa6';
import { handleToggleFavorites } from '../../containers/ListingPage/ListingPage.shared';
import { richText } from '../../util/richText';

import css from './ListingCard.module.css';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { updateProfile } from '../../ducks/user.duck';
import { useHistory, useLocation } from 'react-router-dom';
// Context
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

const { Money } = sdkTypes;

// === Commission Constants ===
const MIN_CUSTOMER_COMMISSION_AMOUNT = 19900; // $199 in cents
const CUSTOMER_COMMISSION_PERCENTAGE = 20; // 20%

// Add the constant
const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

/**
 * Helper function to calculate the customer commission.
 * Returns the greater of 20% of the base price or $150.
 *
 * @param {Money} basePrice - The base price as a Money object.
 * @returns {Money} - The commission as a Money object.
 */
const calculateCustomerCommission = basePrice => {
  const { amount, currency } = basePrice;

  // Calculate 20% of the base price
  const percentageCommissionAmount = Math.round((CUSTOMER_COMMISSION_PERCENTAGE / 100) * amount);

  // Determine the greater value between 20% commission and $150
  const commissionAmount =
    percentageCommissionAmount >= MIN_CUSTOMER_COMMISSION_AMOUNT
      ? percentageCommissionAmount
      : MIN_CUSTOMER_COMMISSION_AMOUNT;

  return new Money(commissionAmount, currency);
};

/**
 * Helper function to calculate the total price (base price + commission).
 *
 * @param {Money} basePrice - The base price as a Money object.
 * @param {Money} commission - The commission as a Money object.
 * @returns {Money} - The total price as a Money object.
 */
const calculateTotalPrice = (basePrice, commission) => {
  const { amount, currency } = basePrice;

  // Sum the base price and commission amounts
  const totalAmount = amount + commission.amount;

  return new Money(totalAmount, currency);
};

const ListingCard = props => {
  const { listing, intl, className, onUpdateFavorites, currentUser, showFavoriteIcon, variant = 'default' } = props;

  const location = useLocation();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const { title, description, price } = listing.attributes;
  const isFavorite = currentUser?.attributes.profile.privateData.favorites?.includes(
    listing.id.uuid
  );

  const commonParams = {
    params: {
      id: listing.id.uuid,
    },
    history,
    routes: routeConfiguration,
  };

  const onToggleFavorites = handleToggleFavorites({
    ...commonParams,
    currentUser,
    onUpdateFavorites,
    location,
  });

  let formattedBasePrice = null;
  let formattedCommission = null;
  let formattedTotalPrice = null;

  if (price && typeof price.amount === 'number' && typeof price.currency === 'string') {
    try {
      const moneyObject = new Money(price.amount, price.currency);

      // Calculate the commission
      const commission = calculateCustomerCommission(moneyObject);

      // Calculate the total price
      const totalPrice = calculateTotalPrice(moneyObject, commission);

      // Format the base price, commission, and total price
      formattedBasePrice = formatMoney(intl, moneyObject);
      formattedCommission = formatMoney(intl, commission);
      formattedTotalPrice = formatMoney(intl, totalPrice);
    } catch (error) {
      console.error('Error formatting price:', error);
    }
  }

  const slug = title ? encodeURIComponent(title.toLowerCase().replace(/\s+/g, '-')) : 'listing';

  const firstImage = listing.images && listing.images.length > 0 ? listing.images[0] : null;

  let imageUrl = null;
  if (firstImage && firstImage.attributes && firstImage.attributes.variants) {
    const variants = firstImage.attributes.variants;
    imageUrl = variants['landscape-crop'] ? variants['landscape-crop'].url : null;
    if (!imageUrl) {
      const firstVariantKey = Object.keys(variants)[0];
      imageUrl = firstVariantKey ? variants[firstVariantKey].url : null;
    }
  }

  // Simplified card for favorites page
  if (variant === 'favorite') {
    return (
      <NamedLink
        className={`${className} ${css.favoriteCard}`}
        name="ListingPage"
        params={{ id: listing.id.uuid, slug }}
      >
        <div className={css.imageWrapper}>
          {imageUrl ? (
            <img src={imageUrl} alt={title} className={css.image} />
          ) : (
            <div className={css.noImageWrapper}>No image available</div>
          )}
          <div className={css.mobileHeartIcon}>
            <div
              className={`${css.iconContainer} ${isFavorite && css.activeContainer}`}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onToggleFavorites(isFavorite);
              }}
            >
              {isFavorite ? (
                <FaHeart className={`${css.favoriteIcon} ${css.ActiveIcon}`} size={20} />
              ) : (
                <FaRegHeart className={css.favoriteIcon} size={20} />
              )}
            </div>
          </div>
        </div>
        <div className={css.favoriteInfo}>
          <div className={css.titleRow}>
            <h3 className={css.title}>{title || 'Untitled Listing'}</h3>
            <div className={css.price}>{formattedTotalPrice}</div>
          </div>
          {description && (
            <div className={css.description}>
              {truncateText(description, 100)}
            </div>
          )}
          <button className={css.viewButton}>View Full Profile</button>
        </div>
      </NamedLink>
    );
  }

  // Default card (original version with all features)
  return (
    <NamedLink
      className={`${className} ${css.listingCard}`}
      name="ListingPage"
      params={{ id: listing.id.uuid, slug }}
    >
      <div className={css.imageWrapper}>
        {imageUrl ? (
          <img src={imageUrl} alt={title} className={css.image} />
        ) : (
          <div className={css.noImageWrapper}>No image available</div>
        )}
      </div>
      <div className={css.info}>
        <div className={css.topInfo}>
          <div className={css.titleRow}>
            <div className={css.title}>{title}</div>
            {showFavoriteIcon && (
              <div
                className={`${css.iconContainer} ${isFavorite && css.activeContainer}`}
                onClick={e => {
                  e.preventDefault();
                  onToggleFavorites(isFavorite);
                }}
              >
                {isFavorite ? (
                  <FaHeart className={`${css.favoriteIcon} ${css.ActiveIcon}`} size={20} />
                ) : (
                  <FaRegHeart className={css.favoriteIcon} size={20} />
                )}
              </div>
            )}
          </div>
          <div className={css.priceRow}>
            <span className={css.price}>{formattedTotalPrice}</span>
            <span className={css.availability}>Available</span>
          </div>
          {description && (
            <div className={css.description}>{description}</div>
          )}
        </div>

        <div className={css.buttonContainer}>
          <button className={css.viewButton}>View Full Profile</button>
          <button className={css.matchButton}>Collaboration Details</button>
        </div>
      </div>
    </NamedLink>
  );
};

ListingCard.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: PropTypes.oneOfType([
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      // other listing props...
    }),
    PropTypes.shape({
      id: PropTypes.object,
      // other listing props...
    }),
  ]),
  showFavoriteIcon: bool,
  variant: string,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;

  return {
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateFavorites: payload => dispatch(updateProfile(payload)),
});

const ListingCardWithState = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ListingCard);
export default ListingCardWithState;
// export default compose(connectinjectIntl(ListingCard));
